import { createStore } from 'solid-js/store';
import { gql } from 'graphql-request';
import { Accessor, createMemo, Show, useContext } from 'solid-js';
import { Grid } from '../../grid-system/grid/grid';
import { Section } from '../../grid-system/section/section';
import { ErrorCatcher } from '../../tools/error-catcher';
import { gridSettings } from '../../ui-components/layouts/sidebar.style';
import { EventsContainer } from './events-container';
import { EventsSidebar } from './events-sidebar';
import { EventsProps } from './events-types';
import { fetchEvents } from './helpers/fetch-events';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { useNavigate } from '@solidjs/router';
import createClickHelper from './helpers/filters-handle-on-click-helper';
import { EventListCardData, EventListDataContainer } from '../event/event-types';
import { LoadingPlaceHolder } from '../loading-place-holder/loading-place-holder';
import { SiteDomain } from '../../types/app-state';
import { isOnDemandEvent } from './helpers/is-on-demand-event';
import { mapEventMetaToListCardData } from './helpers/map-event-meta-to-list-card-data';

const GET_EVENTS_QUERY = gql`
    query GetEvents(
        $siteId: String,
        $type: [String],
        $tags: [String],
        $limit: Int,
        $fromDate: String,
        $toDate: String
    ) {
        events: resources(
            siteId: $siteId,
            type: $type,
            tags: $tags,
            limit: $limit,
            order: DESC,
            fromDate: $fromDate,
            toDate: $toDate
        ) {
            key
            meta {
                postTitle
                categories
                description
                featuredImage
                extra
            }
        }
    }
`;

const GET_MIRRORED_EVENTS_KEYS_QUERY = gql`
    query GetMirroredEvents($key: String!) {
        resource(key: $key) {
            content
            key
        }
    }
`;

type SiteEventsObject = {
    siteDomain: SiteDomain;
    siteEvents: Accessor<{ events: EventListDataContainer[] }>;
};

export const Events = (props: EventsProps) => {
    const navigate = useNavigate();

    const { 
        siteDomains,
        createCachedResource,
        siteInfo,
    } = useContext(AppContext);

    const [ eventsBySite, setEventsBySite ] = createStore<{ data: SiteEventsObject[]; }>({ data: [] });

    const [ mirroredEventsKeys ] = createCachedResource(GET_MIRRORED_EVENTS_KEYS_QUERY, () => ({ key: '/wp-json/rawb/v1/mirrored-global-events-keys' }), true);

    const clickHelper = createClickHelper(navigate);

    const internationalSite = siteDomains.find((site) => site.country === 'international');
    const isInternationalSite = siteInfo.siteId === internationalSite?.siteId;

    const nationalEventsData = fetchEvents({});
    const [ internationalSiteEvents ] = internationalSite?.siteId
        ? createCachedResource(GET_EVENTS_QUERY, () => ({ siteId: internationalSite.siteId, type: [ 'event' ] }), true) as any 
        : [null];

    const fetchAllSitesEvents = () => {
        // If national site we don't need to fetch any more - we already have national and international events via fetchEvents and internationalSiteEvents
        if (!isInternationalSite) {
            return;
        }
        
        /**
         * If international site, fetch all events from all other sites.
         * We could omit the international site from this loop as it has already been fetched, both via fetchEvents and internationalSiteEvents, but
         * it seems to break reactivity, so we include it here as well.
         */
        for (const site of siteDomains) {
            const [ siteEvents ] = createCachedResource(GET_EVENTS_QUERY, () => ({ siteId: site.siteId, type: [ 'event' ] }), true) as any;

            const siteEventsObject: SiteEventsObject = {
                siteDomain: site,
                siteEvents,
            };

            setEventsBySite('data', (s) => [...s, siteEventsObject]);
        }
    };

    fetchAllSitesEvents();

    const sortEvents = (events: EventListCardData[]) => {
        return events
            .filter((event: EventListCardData) => {
                // If there is no eventDate, we assume it is an on-demand event - if it is not, it should be filtered out (bad content)
                return event?.eventDate || isOnDemandEvent(event);
            })
            .sort((a: EventListCardData, b: EventListCardData) => {
                // We sort by date, not eventDate, in order to sort on-demand events simultaneously
                const dateA = a.date ? new Date(a.date).getTime() : 0; // Use 0 as default if a.date is undefined
                const dateB = b.date ? new Date(b.date).getTime() : 0; // Use 0 as default if b.date is undefined
                return dateA - dateB;
            });
    };

    const getFlatlistOfAllEventsFromAllSites = createMemo(() => {
        const loaded = eventsBySite.data.reduce((acc: any, site: any) => {
            return acc && Boolean(site.siteEvents());
        }, true);

        if (!loaded) {
            return [];
        }

        const flatList = eventsBySite.data.reduce((acc: EventListCardData[], site: SiteEventsObject) => {
            const siteEvents = site.siteEvents()?.events.map((event: EventListDataContainer) => {
                const mappedEvent = mapEventMetaToListCardData(event);

                const baseUrl = site.siteDomain.domains[ siteInfo.target ];
                
                const url = internationalSite?.siteId === site.siteDomain.siteId
                    ? mappedEvent.url
                    : `${siteInfo.protocol}://${baseUrl}/${mappedEvent.url}`;
    
                const newEvent = {
                    ...mappedEvent,
                    url,
                };

                return newEvent;
            });

            return [...acc, ...siteEvents];
        }, []);

        return sortEvents(flatList);
    });

    const filterByAudience = (post: EventListCardData) => {
        switch (props.audience) {
            case 'all': return true;
            case 'patients': return !post?.isHealthcareProfessional;
            case 'hcp': return post?.isHealthcareProfessional;
            default: return true;
        };
    };

    const getCountryEvents = () => {
        const mirroredEvents = getMirroredEvents();

        if (!nationalEventsData()?.length) {
            return mirroredEvents || [];
        }

        // Weave together mirrored events with country events
        const countryEvents = nationalEventsData()?.concat(mirroredEvents);
        return sortEvents(countryEvents);
    };

    const getMirroredEvents = () => {
        const mirrored = mirroredEventsKeys()?.resource?.content;

        if (!mirrored?.length) {
            return [];
        }
        if (!(internationalSiteEvents()?.events as EventListDataContainer[])?.length) {
            return [];
        }

        return (internationalSiteEvents()?.events as EventListDataContainer[])
            .map((globalEvent) => {
                return mapEventMetaToListCardData(globalEvent);
            })
            .filter((event) => {
                return !event.disableMirroringInCountries;
            })
            .filter((event) => {
                return mirrored.find((mirroredEventKey: string) => mirroredEventKey === event.permalink);
            })
            .map((event) => {
                const baseUrl = internationalSite?.domains[ siteInfo.target ];

                return {
                    ...event,
                    url: `${siteInfo.protocol}://${baseUrl}/${event.url}`,
                };
            });
    };

    const filteredEvents = createMemo(() => {
        const events: EventListCardData[] = isInternationalSite ? getFlatlistOfAllEventsFromAllSites() : getCountryEvents();
        const filteredByAudience = events?.filter(filterByAudience);
        return filteredByAudience;
    });
  
    return (
        <ErrorCatcher componentName="Events list">
            <Show when={filteredEvents()} fallback={<LoadingPlaceHolder />}>
                <Section
                    templateShorthand={[12]}
                    widthType={'bgFull'}
                    heightType={'fill'}
                    backgroundType={'color'}
                    backgroundValue={'white'}
                    removeSpacingBlock={true}
                    customCss={'padding-top: 0; padding-bottom: 0;'}
                >
                    <Grid {...gridSettings.container}>
                        <Show when={filteredEvents().length}>
                            <EventsSidebar
                                events={filteredEvents()}
                                labels={props?.labels}
                                clickHelper={clickHelper}
                                isInternationalSite={isInternationalSite}
                            />

                            <EventsContainer
                                events={filteredEvents()}
                                labels={props?.labels}
                                clickHelper={clickHelper}
                                isInternationalSite={isInternationalSite}
                            />
                        </Show>
                    </Grid>
                </Section>
            </Show>
        </ErrorCatcher>
    );
};

Events.parseProps = (atts: any) => {
    return {
        tag: atts.tag,
        labels: atts.labels,
        audience: atts.audience,
    };
};
